// Member notes (architecture sprint-12 §7.2, §9.2). Every key sits under ['oversight', projectId, 'notes'], so a
// membership change (which invalidates ['oversight']) also refreshes the subjects list.
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { LocalDate } from '@shared/calendar';
import { apiRequest, apiRequestNoContent, downloadFile } from '@/lib/api';
import type { Note, NoteFilters, NoteHistory, NotePage, NoteSubject } from '@/types/notes';

const NOTES_PAGE_SIZE = 50;

export const notesKey = (projectId: string): readonly unknown[] => ['oversight', projectId, 'notes'];
const notesPath = (projectId: string): string => `/api/oversight/projects/${projectId}/notes`;

/** Lower-case, non-alphanumerics to '-', trimmed. Only the fallback filename uses it; the server sends its own. */
export function noteSlug(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'project';
}

export function notesExportFilename(projectName: string, from: LocalDate, to: LocalDate, format: 'csv' | 'xlsx'): string {
  return `clockit-notes_${noteSlug(projectName)}_${from}_${to}.${format}`;
}

export function notesQuery(filters: NoteFilters, cursor: string | null): string {
  // Sprint 15 (§7.3): To review and the shift filter send no dates (every pending note / the whole shift).
  const dated = !filters.review && !filters.shiftId;
  const q = new URLSearchParams(dated ? { from: filters.from, to: filters.to } : {});
  if (filters.review) q.set('review', 'pending');
  if (filters.shiftId) q.set('shiftId', filters.shiftId);
  if (filters.userId) q.set('userId', filters.userId);
  if (filters.includeDeleted) q.set('includeDeleted', 'true');
  if (cursor) q.set('cursor', cursor);
  q.set('limit', String(NOTES_PAGE_SIZE));
  return q.toString();
}

export function useNoteSubjects(projectId: string, enabled = true): UseQueryResult<NoteSubject[]> {
  return useQuery({
    queryKey: [...notesKey(projectId), 'subjects'],
    queryFn: async () => (await apiRequest<{ items: NoteSubject[] }>(`${notesPath(projectId)}/subjects`)).items,
    enabled,
  });
}

export function useNotes(projectId: string, filters: NoteFilters, enabled: boolean): UseInfiniteQueryResult<InfiniteData<NotePage>> {
  return useInfiniteQuery({
    queryKey: [...notesKey(projectId), 'list', filters],
    queryFn: ({ pageParam }) => apiRequest<NotePage>(`${notesPath(projectId)}?${notesQuery(filters, pageParam)}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
  });
}

export function useNoteHistory(projectId: string, noteId: string | null): UseQueryResult<NoteHistory> {
  return useQuery({
    queryKey: [...notesKey(projectId), 'history', noteId],
    queryFn: () => apiRequest<NoteHistory>(`${notesPath(projectId)}/${noteId ?? ''}/revisions`),
    enabled: noteId !== null,
  });
}

function useInvalidateNotes(projectId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: notesKey(projectId) });
}

export interface NoteInput {
  userId: string;
  date: LocalDate;
  description: string;
  /** Sprint 13 (§13): the linked shift; null clears it on edit. */
  shiftId?: string | null;
}

/** Errors are shown inline by the dialog (meta.silent). */
export function useCreateNote(projectId: string): UseMutationResult<{ note: Note }, Error, NoteInput> {
  const invalidate = useInvalidateNotes(projectId);
  return useMutation({
    mutationFn: (body) => apiRequest(notesPath(projectId), { method: 'POST', body: JSON.stringify(body) }),
    meta: { silent: true },
    onSuccess: invalidate,
  });
}

export function useUpdateNote(projectId: string): UseMutationResult<{ note: Note }, Error, { noteId: string; patch: Partial<NoteInput> }> {
  const invalidate = useInvalidateNotes(projectId);
  return useMutation({
    mutationFn: ({ noteId, patch }) => apiRequest(`${notesPath(projectId)}/${noteId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    meta: { silent: true },
    onSuccess: invalidate,
  });
}

export function useDeleteNote(projectId: string): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateNotes(projectId);
  return useMutation({
    mutationFn: (noteId) => apiRequestNoContent(`${notesPath(projectId)}/${noteId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useExportNotes(projectId: string, projectName: string): UseMutationResult<void, Error, { filters: NoteFilters; format: 'csv' | 'xlsx' }> {
  return useMutation({
    mutationFn: ({ filters, format }) => {
      const q = new URLSearchParams({ from: filters.from, to: filters.to, format });
      if (filters.userId) q.set('userId', filters.userId);
      return downloadFile(`${notesPath(projectId)}/export?${q.toString()}`, notesExportFilename(projectName, filters.from, filters.to, format));
    },
  });
}
